import React, { useEffect, useState } from 'react';
import { Routes, Route, Link, useNavigate, useLocation } from 'react-router-dom';
import UsersManagement from '../components/admin/UsersManagement';
import PlausibleData from '../components/admin/PlausibleData';
import BIConfigManagement from '../components/admin/BIConfigManagement';

const AdminPage = ({ fetchWithAuth, currentUser }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    // Redirect if not admin
    if (!currentUser?.isadmin && !currentUser?.issuperadmin) {
      navigate('/');
    }

    // Set active tab based on URL
    const path = location.pathname.split('/').pop();
    if (path && path !== 'admin') {
      setActiveTab(path);
    } else {
      setActiveTab('dashboard');
    }
  }, [currentUser, navigate, location]);

  const AdminDashboard = () => (
    <div className="p-6">
      <h2 className="text-2xl font-bold mb-6 text-gray-900 dark:text-gray-100">Tableau de bord administrateur</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-md">
          <h3 className="text-lg font-semibold mb-2 text-gray-900 dark:text-gray-100">Statistiques</h3>
          <p className="text-gray-600 dark:text-gray-300 mb-4">Consulter les statistiques d'utilisation</p>
          <button
            onClick={() => navigate('/admin/analytics')}
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
          >
            Voir les statistiques
          </button>
        </div>

        {currentUser?.issuperadmin && (
          <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-md">
            <h3 className="text-lg font-semibold mb-2 text-gray-900 dark:text-gray-100">Utilisateurs</h3>
            <p className="text-gray-600 dark:text-gray-300 mb-4">Gérer les utilisateurs</p>
            <button
            onClick={() => navigate('/admin/users')}
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
          >
            Voir les utilisateurs
            </button>
          </div>
        )}

        {currentUser?.issuperadmin && (
          <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-md">
            <h3 className="text-lg font-semibold mb-2 text-gray-900 dark:text-gray-100">Configuration BI</h3>
            <p className="text-gray-600 dark:text-gray-300 mb-4">Gérer les configurations projet par module</p>
            <button
              onClick={() => navigate('/admin/bi-config')}
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
            >
              Voir les configurations
            </button>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div className="flex flex-col md:flex-row h-full bg-gray-100 dark:bg-gray-900">
      {/* Mobile menu button */}
      <div className="md:hidden bg-gray-800 dark:bg-gray-800 p-4 shadow-md">
        <button
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          className="flex items-center justify-center w-full px-4 py-2 text-sm font-medium text-white dark:text-gray-200 bg-gray-700 dark:bg-gray-700 rounded-md hover:bg-gray-600 dark:hover:bg-gray-600 focus:outline-none"
        >
          <span>Menu</span>
          <svg
            className={`ml-2 h-5 w-5 transform transition-transform duration-200 ${isMobileMenuOpen ? 'rotate-180' : ''}`}
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
          </svg>
        </button>
      </div>

      {/* Sidebar */}
      <div
        className={`${
          isMobileMenuOpen ? 'block' : 'hidden'
        } md:block w-full md:w-72 bg-gray-800 dark:bg-gray-800 text-white md:h-full flex-shrink-0`}
      >
        <div className="p-6">
          {/* <h2 className="text-xl font-bold mb-8 text-white">Administration</h2> */}
          <nav className="space-y-2">
            <Link
              to="/admin"
              onClick={() => setIsMobileMenuOpen(false)}
              className={`flex items-center px-4 py-3 rounded-md text-sm font-medium transition-colors ${
                activeTab === 'dashboard'
                  ? 'bg-blue-600 text-white'
                  : 'text-gray-300 hover:bg-gray-700 hover:text-white'
              }`}
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
              </svg>
              Tableau de bord
            </Link>
            <Link
              to="/admin/analytics"
              onClick={() => setIsMobileMenuOpen(false)}
              className={`flex items-center px-4 py-3 rounded-md text-sm font-medium transition-colors ${
                activeTab === 'analytics'
                  ? 'bg-blue-600 text-white'
                  : 'text-gray-300 hover:bg-gray-700 hover:text-white'
              }`}
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
              Statistiques
            </Link>

            {currentUser?.issuperadmin && (
              <Link
                to="/admin/users"
                onClick={() => setIsMobileMenuOpen(false)}
                className={`flex items-center px-4 py-3 rounded-md text-sm font-medium transition-colors ${
                  activeTab === 'users'
                    ? 'bg-blue-600 text-white'
                    : 'text-gray-300 hover:bg-gray-700 hover:text-white'
                }`}
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                </svg>
                  Utilisateurs
              </Link>
            )}

            {currentUser?.issuperadmin && (
              <Link
                to="/admin/bi-config"
                onClick={() => setIsMobileMenuOpen(false)}
                className={`flex items-center px-4 py-3 rounded-md text-sm font-medium transition-colors ${
                  activeTab === 'bi-config'
                    ? 'bg-blue-600 text-white'
                    : 'text-gray-300 hover:bg-gray-700 hover:text-white'
                }`}
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 7h16M4 12h16M4 17h16" />
                </svg>
                Configuration BI
              </Link>
            )}
          </nav>
        </div>
      </div>

      {/* Main content */}
      <div className="flex-1 h-full overflow-hidden flex flex-col">
        <Routes>
          <Route index element={<AdminDashboard />} />
          <Route path="users" element={<UsersManagement fetchWithAuth={fetchWithAuth} />} />
          <Route path="analytics" element={<PlausibleData />} />
          <Route path="bi-config" element={<BIConfigManagement fetchWithAuth={fetchWithAuth} />} />
        </Routes>
      </div>
    </div>
  );
};

export default AdminPage;